import {
  BENEFIT_REDEMPTION_METHODS,
  BenefitMetricsRedemptionsByDay,
  BenefitMetricsRedemptionsByMethod,
  BenefitMetricsTenantBreakdown,
  BenefitRedemptionMethod,
} from "../../models/benefitsMetrics";

const REDEMPTION_METHOD_LABELS: Record<BenefitRedemptionMethod, string> = {
  visual_credential: "Credencial visual",
  promo_code: "Código promo",
  qr_dynamic: "QR dinámico",
  pin: "PIN",
  activation_link: "Link de activación",
};

const REDEMPTION_METHOD_COLORS: Record<BenefitRedemptionMethod, string> = {
  visual_credential: "#36A2EB",
  promo_code: "#4BC0C0",
  qr_dynamic: "#FF9F40",
  pin: "#9966FF",
  activation_link: "#FF6384",
};

export const DEFAULT_SINGLE_SERIES_COLOR = "#36A2EB";

// Vista "Todos": paleta categórica distinta por tenant (no es marca). El slot
// sigue el orden de by_tenant (nombre) para que no salte al mover el rango.
export const TENANT_SERIES_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];

export const OTHER_TENANTS_COLOR = "#898781";
export const OTHER_TENANTS_LABEL = "Otros";
const CHART_SURFACE = "#ffffff";

interface BenefitsMetricsTenantSeries {
  key: string;
  label: string;
  color: string;
  redemptions: number;
  redemptionsByDay: BenefitMetricsRedemptionsByDay[];
  redemptionsByMethod: BenefitMetricsRedemptionsByMethod;
}

const emptyRedemptionsByMethod = (): BenefitMetricsRedemptionsByMethod => ({
  visual_credential: 0,
  promo_code: 0,
  qr_dynamic: 0,
  pin: 0,
  activation_link: 0,
});

const tenantLabel = (tenant: BenefitMetricsTenantBreakdown): string =>
  tenant.tenant_name ?? tenant.tenant_id;

const mergeRedemptionsByDay = (
  groups: BenefitMetricsRedemptionsByDay[][]
): BenefitMetricsRedemptionsByDay[] => {
  const totals = new Map<string, number>();
  groups.forEach((group) => {
    group.forEach((item) => {
      totals.set(item.date, (totals.get(item.date) ?? 0) + item.count);
    });
  });
  return Array.from(totals.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

const mergeRedemptionsByMethod = (
  groups: BenefitMetricsRedemptionsByMethod[]
): BenefitMetricsRedemptionsByMethod => {
  const totals = emptyRedemptionsByMethod();
  groups.forEach((group) => {
    BENEFIT_REDEMPTION_METHODS.forEach((method) => {
      totals[method] += group?.[method] ?? 0;
    });
  });
  return totals;
};

export const tenantSeriesColor = (index: number): string =>
  TENANT_SERIES_COLORS[index % TENANT_SERIES_COLORS.length];

/**
 * Series para la vista "Todos": un color distinto por tenant desde la paleta
 * categórica. No usa el color de marca del tenant.
 */
export const buildBenefitsMetricsTenantSeries = (
  byTenant: BenefitMetricsTenantBreakdown[] | null | undefined
): BenefitsMetricsTenantSeries[] => {
  const tenants = byTenant ?? [];
  const withData = tenants
    .map((tenant, index) => ({ tenant, index }))
    .filter((item) => item.tenant.redemptions > 0);

  // El corte va por posicion entre los tenants CON datos: cortando por el indice crudo de
  // by_tenant, tres tenants que canjearon pero estan del octavo lugar en adelante caian
  // todos juntos en "Otros" habiendo colores libres.
  const named = withData.slice(0, TENANT_SERIES_COLORS.length);
  const overflow = withData.slice(TENANT_SERIES_COLORS.length);

  const series: BenefitsMetricsTenantSeries[] = named.map(({ tenant, index }) => ({
    key: tenant.tenant_id,
    label: tenantLabel(tenant),
    color: tenantSeriesColor(index),
    redemptions: tenant.redemptions,
    redemptionsByDay: tenant.redemptions_by_day ?? [],
    redemptionsByMethod: tenant.redemptions_by_method ?? emptyRedemptionsByMethod(),
  }));

  if (overflow.length === 0) {
    return series;
  }

  return [
    ...series,
    {
      key: OTHER_TENANTS_LABEL,
      label: `${OTHER_TENANTS_LABEL} (${overflow.length})`,
      color: OTHER_TENANTS_COLOR,
      redemptions: overflow.reduce((total, item) => total + item.tenant.redemptions, 0),
      redemptionsByDay: mergeRedemptionsByDay(
        overflow.map((item) => item.tenant.redemptions_by_day ?? [])
      ),
      redemptionsByMethod: mergeRedemptionsByMethod(
        overflow.map((item) => item.tenant.redemptions_by_method)
      ),
    },
  ];
};

export const hasBenefitsMetricsTenantBreakdown = (
  series: BenefitsMetricsTenantSeries[]
): boolean => series.length > 0;

const formatRedemptionsTooltip = (value: number | null): string => {
  const count = value ?? 0;
  return ` ${count} ${count === 1 ? "canje" : "canjes"}`;
};

const formatTenantTooltip = (label: string, value: number | null): string => {
  const count = value ?? 0;
  return ` ${label}: ${count} ${count === 1 ? "canje" : "canjes"}`;
};

const hasPositiveTooltipValue = (value: number | null | undefined): boolean =>
  typeof value === "number" && value > 0;

interface TenantDayTooltipItem {
  dataset: { label?: string };
  parsed: { y: number | null };
}

const dayChartTicks = {
  maxRotation: 0,
  minRotation: 0,
  autoSkip: true,
  maxTicksLimit: 8,
};

/** Mismo trazo para un tenant y para la vista Todos: una linea con sus puntos. */
export const dayLineDataset = (label: string, color: string, data: number[]) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  pointBackgroundColor: color,
  pointBorderColor: CHART_SURFACE,
  pointBorderWidth: 2,
  pointRadius: 7,
  pointHoverRadius: 9,
  pointHitRadius: 16,
  borderWidth: 6,
  tension: 0.25,
  fill: false,
});

// Hover punto a punto: solo el/los tenants que estan abajo del cursor.
export const dayChartInteraction = {
  mode: "point" as const,
  intersect: true,
};

export const dayChartScales = {
  x: {
    // Centra los puntos en la banda del dia; sin esto el primero y el ultimo quedan
    // pegados a los bordes del chart.
    offset: true,
    grid: {
      display: false,
    },
    ticks: dayChartTicks,
  },
  y: {
    beginAtZero: true,
    ticks: {
      precision: 0,
    },
  },
};

export const tenantDayLegend = {
  display: true,
  position: "top" as const,
  align: "start" as const,
  labels: {
    boxWidth: 16,
    boxHeight: 12,
    padding: 16,
    usePointStyle: true,
    pointStyle: "circle" as const,
  },
};

export const benefitsMetricsChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      bottom: 8,
    },
  },
  interaction: dayChartInteraction,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      callbacks: {
        label: (context: { parsed: { y: number | null } }) =>
          formatRedemptionsTooltip(context.parsed.y),
      },
    },
  },
  scales: dayChartScales,
};

export const benefitsMetricsTenantDayChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 8,
      bottom: 8,
    },
  },
  interaction: dayChartInteraction,
  plugins: {
    legend: tenantDayLegend,
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      callbacks: {
        label: (context: TenantDayTooltipItem) =>
          formatTenantTooltip(context.dataset.label ?? "", context.parsed.y),
      },
    },
  },
  scales: dayChartScales,
};

const tenantMethodLegend = {
  display: true,
  position: "top" as const,
  align: "start" as const,
  labels: {
    boxWidth: 16,
    boxHeight: 12,
    padding: 16,
  },
};

export const benefitsMetricsMethodChartOptions = {
  indexAxis: "y" as const,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 4,
      bottom: 4,
      left: 4,
      right: 12,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context: { parsed: { x: number | null } }) =>
          formatRedemptionsTooltip(context.parsed.x),
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
    y: {
      ticks: {
        autoSkip: false,
        font: {
          size: 12,
        },
      },
    },
  },
};

export const benefitsMetricsTenantMethodChartOptions = {
  indexAxis: "y" as const,
  maintainAspectRatio: false,
  // Sin esto cada metodo reserva un slot por tenant, canjee o no, y las barras salen
  // finitas con huecos al lado.
  skipNull: true,
  layout: {
    padding: {
      top: 4,
      bottom: 4,
      left: 4,
      right: 12,
    },
  },
  interaction: {
    mode: "point" as const,
    intersect: true,
  },
  plugins: {
    legend: tenantMethodLegend,
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      filter: (context: { parsed: { x: number | null } }) =>
        hasPositiveTooltipValue(context.parsed.x),
      callbacks: {
        label: (context: { dataset: { label?: string }; parsed: { x: number | null } }) =>
          formatTenantTooltip(context.dataset.label ?? "", context.parsed.x),
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
    y: {
      ticks: {
        autoSkip: false,
        font: {
          size: 12,
        },
      },
    },
  },
};

export const buildBenefitsMetricsDetailPath = (params: {
  partnerId?: string;
  dateFrom?: string;
  dateTo?: string;
}): string => {
  const search = new URLSearchParams();
  if (params.partnerId) {
    search.set("partnerId", params.partnerId);
  }
  if (params.dateFrom) {
    search.set("dateFrom", params.dateFrom);
  }
  if (params.dateTo) {
    search.set("dateTo", params.dateTo);
  }
  const query = search.toString();
  return query ? `/benefits/metrics/detail?${query}` : "/benefits/metrics/detail";
};

export const formatDayLabel = (rawDate: string): string => {
  const parsed = new Date(rawDate.includes("T") ? rawDate : `${rawDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return rawDate;
  }
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

interface BenefitsMetricsDayRange {
  dateFrom: Date | null;
  dateTo: Date | null;
}

const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/**
 * El backend manda solo los dias con canjes. Con uno solo, la serie es un punto suelto y
 * no hay linea: se agregan los bordes del rango elegido en 0 para que tenga de donde
 * colgar. La vista Todos no lo necesita porque ya rellena en 0 los dias de los otros
 * tenants.
 */
const withRangeEdges = (
  redemptionsByDay: BenefitMetricsRedemptionsByDay[],
  range?: BenefitsMetricsDayRange | null
): BenefitMetricsRedemptionsByDay[] => {
  if (!range?.dateFrom || !range?.dateTo) {
    return redemptionsByDay;
  }

  const byDay = new Map(
    redemptionsByDay.map((item) => [item.date.slice(0, 10), item])
  );
  [dayKey(range.dateFrom), dayKey(range.dateTo)].forEach((key) => {
    if (!byDay.has(key)) {
      byDay.set(key, { date: key, count: 0 });
    }
  });

  return Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));
};

export const buildRedemptionsByDayChartData = (
  redemptionsByDay: BenefitMetricsRedemptionsByDay[] | null | undefined,
  seriesColor?: string | null,
  range?: BenefitsMetricsDayRange | null
) => {
  const days = withRangeEdges(redemptionsByDay ?? [], range);
  return {
    labels: days.map((item) => formatDayLabel(item.date)),
    datasets: [
      dayLineDataset(
        "Canjes",
        seriesColor || DEFAULT_SINGLE_SERIES_COLOR,
        days.map((item) => item.count)
      ),
    ],
  };
};

export const buildTenantRedemptionsByDayChartData = (
  series: BenefitsMetricsTenantSeries[]
) => {
  const dates = Array.from(
    new Set(
      series.flatMap((item) => item.redemptionsByDay.map((day) => day.date))
    )
  ).sort((a, b) => a.localeCompare(b));

  const countsFor = (item: BenefitsMetricsTenantSeries) => {
    const countsByDate = new Map(
      item.redemptionsByDay.map((day) => [day.date, day.count])
    );
    return dates.map((date) => countsByDate.get(date) ?? 0);
  };

  return {
    labels: dates.map(formatDayLabel),
    datasets: series.map((item) =>
      dayLineDataset(item.label, item.color, countsFor(item))
    ),
  };
};

export const buildRedemptionsByMethodChartData = (
  redemptionsByMethod: BenefitMetricsRedemptionsByMethod | undefined,
  seriesColor?: string | null
) => {
  const methods = BENEFIT_REDEMPTION_METHODS;
  return {
    labels: methods.map((method) => REDEMPTION_METHOD_LABELS[method]),
    datasets: [
      {
        label: "Canjes",
        data: methods.map((method) => redemptionsByMethod?.[method] ?? 0),
        backgroundColor: seriesColor
          ? methods.map(() => seriesColor)
          : methods.map((method) => REDEMPTION_METHOD_COLORS[method]),
        borderRadius: 4,
        maxBarThickness: 28,
      },
    ],
  };
};

export const buildTenantRedemptionsByMethodChartData = (
  series: BenefitsMetricsTenantSeries[]
) => ({
  labels: BENEFIT_REDEMPTION_METHODS.map((method) => REDEMPTION_METHOD_LABELS[method]),
  // null, no 0: con `skipNull` el tenant que no canjeo ese metodo no reserva lugar, asi
  // que el metodo que canjeo uno solo se lleva toda la fila.
  datasets: series.map((item) => ({
    label: item.label,
    data: BENEFIT_REDEMPTION_METHODS.map((method) => {
      const count = item.redemptionsByMethod?.[method] ?? 0;
      return count > 0 ? count : null;
    }),
    backgroundColor: item.color,
    borderColor: CHART_SURFACE,
    borderWidth: 1,
    borderSkipped: false,
    borderRadius: 4,
    // Mas alto que el ancho natural de una fila con un solo tenant (~34px), para que la
    // llene en vez de quedar recortada con blanco arriba y abajo.
    maxBarThickness: 44,
  })),
});

export const hasRedemptionsByMethod = (
  redemptionsByMethod: BenefitMetricsRedemptionsByMethod | undefined
): boolean => {
  if (!redemptionsByMethod) {
    return false;
  }
  return BENEFIT_REDEMPTION_METHODS.some(
    (method) => (redemptionsByMethod[method] ?? 0) > 0
  );
};

export const formatBenefitsMetricsShare = (
  value: number | null | undefined,
  total: number | null | undefined
): string => {
  if (
    typeof value !== "number" ||
    typeof total !== "number" ||
    total <= 0 ||
    Number.isNaN(value)
  ) {
    return "—";
  }
  return `${((value / total) * 100).toFixed(1)}%`;
};

/**
 * Alto del chart de metodos agrupado. chart.js reparte todas las bandas por igual, asi que
 * manda el metodo con mas tenants: si en uno canjearon 20, esa banda tiene que entrar 20
 * barras y las demas quedan del mismo alto.
 */
export const tenantMethodChartHeight = (
  series: BenefitsMetricsTenantSeries[]
): number => {
  const tenantsPorMetodo = BENEFIT_REDEMPTION_METHODS.map(
    (method) =>
      series.filter((item) => (item.redemptionsByMethod?.[method] ?? 0) > 0).length
  );
  const masCargado = Math.max(1, ...tenantsPorMetodo);
  return Math.max(
    320,
    BENEFIT_REDEMPTION_METHODS.length * masCargado * 22 + 75
  );
};
